import { logger } from '@automata/utils';
import {
  ActionTask,
  CodeTask,
  Conditional,
  LabwareSource,
  Location,
  LocationRange,
  ParameterReference,
  ReferenceData,
  ScriptFunctionInfo,
  StaticData,
  StoredLabware,
  TaskArguments,
  TaskOutputReference,
  WorkcellInstrument,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import { InputType } from './types';
import {
  SDKInteropError,
  SdkMisconfigurationError,
  UnsupportedFeatureError,
  UnsupportedScriptError,
} from '../../errors';
import {
  locationToParsedSlots,
  slotFillingRuleToString,
  stringifyParsedSlots,
} from '../../features/shared/utils';
import { getAllLabwareDefinitions } from '../../labware';
import { LabwareDefByDefURI } from '../../labware/types';
import { LabwareType, TaskProperties } from '../../types';
import { DATA_MODE_BY_PARAM } from '../../utils/dataModes';
import { isLabwareType } from '../../zodSchemas/labwareProperties';
import { DataMode, TaskParameterValue } from '../../zodSchemas/taskProperties';

export const convertFromJsonSerializableOutput = (
  input: unknown,
  parentKey: string = ''
): TaskProperties['taskParameters'] => {
  let result: TaskProperties['taskParameters'] = {};

  if (
    typeof input === 'boolean' ||
    typeof input === 'number' ||
    (typeof input === 'string' && input.trim() !== '')
  ) {
    result[parentKey] = input;
  } else if (
    typeof input === 'object' &&
    input !== null &&
    !Array.isArray(input)
  ) {
    for (const [key, value] of Object.entries(input)) {
      const nestedResult = convertFromJsonSerializableOutput(value, key);
      result = {
        ...result,
        ...nestedResult,
      };
    }
  }
  // Skip null values

  return result;
};

export const convertToTaskParameters = (
  input: TaskArguments | null,
  labwareSources?: Record<string, LabwareSource | StoredLabware>
): TaskProperties['taskParameters'] => {
  const safeInput = input || { static: {}, shared: {} };

  const staticParameters: TaskProperties['taskParameters'] = Object.entries(
    safeInput.static
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      ...convertFromJsonSerializableOutput(value, key),
    }),
    {}
  );

  const baseParams = {
    ...staticParameters,
    ...safeInput.shared,
  };

  // Extract slot parameter from labware sources for slot-based tasks
  if (labwareSources) {
    const slotParam = Object.values(labwareSources).find((source) => {
      if ('destination_slot' in source && source.destination_slot !== null) {
        // Include both complex slot rules and single slots that aren't the default (1)
        return (
          typeof source.destination_slot !== 'number' ||
          source.destination_slot !== 1
        );
      }
      return false;
    });

    if (slotParam && 'destination_slot' in slotParam) {
      let slotString: string | null = null;

      if (typeof slotParam.destination_slot === 'number') {
        // Handle single slot numbers (like 5)
        slotString = slotParam.destination_slot.toString();
      } else {
        // Handle complex slot filling rules (like ranges)
        slotString = slotFillingRuleToString(slotParam.destination_slot);
      }

      if (slotString) {
        return {
          ...baseParams,
          slot: slotString,
        };
      }
    }
  }

  return baseParams;
};

export const getSlot = (
  input: Location | LocationRange | Array<Location | LocationRange>
): string => {
  const defaultSlot = '1';

  const parsedSlots = locationToParsedSlots(input);
  const slotString = stringifyParsedSlots(parsedSlots);
  return slotString ?? defaultSlot;
};

export const getLabwareIdsToInputSlots = (
  task: ActionTask
): Record<string, string> =>
  Object.values(task.labware_sources || {}).reduce<Record<string, string>>(
    (acc, source) => {
      if ('destination_slot' in source && source.destination_slot) {
        // Convert to string for slot range support
        if (typeof source.destination_slot === 'number') {
          const slotString = source.destination_slot.toString();
          acc[source.labware_id] = slotString;
        } else {
          // Handle complex slot filling rules by converting to string format
          const slotString = slotFillingRuleToString(source.destination_slot);
          if (slotString) {
            acc[source.labware_id] = slotString;
          }
        }
      } else if ('slot' in source && source.slot !== null) {
        // Handle StoredLabware case - convert to string
        const slotString = source.slot.toString();
        acc[source.labware_id] = slotString;
      }
      return acc;
    },
    {}
  );

export const getLabwareIdsToOutputSlots = (
  task: ActionTask
): Record<string, string> =>
  Object.values(task.labware_outputs || {}).reduce<Record<string, string>>(
    (acc, output) => {
      if ('slot' in output && output.slot !== null) {
        // Convert to string for slot range support
        if (typeof output.slot === 'number') {
          const slotString = output.slot.toString();
          acc[output.labware_id] = slotString;
        } else {
          // OutputSlotFillingRule: convert behavior to string representation
          // This preserves the intent for the UI to potentially handle
          acc[output.labware_id] = output.slot.behavior;
        }
      }
      return acc;
    },
    {}
  );

export const getLabwareIdsToUnitsConsumed = (
  task: ActionTask
): Record<string, number> =>
  Object.values(task.labware_sources || {}).reduce<Record<string, number>>(
    (acc, source) => {
      if ('units_consumed' in source && source.units_consumed !== null) {
        const currentTotal = acc[source.labware_id] || 0;
        acc[source.labware_id] = currentTotal + source.units_consumed;
      }
      return acc;
    },
    {}
  );

export const getInstrumentForTask = (
  task: ActionTask,
  workcellInstruments: WorkcellInstrument[]
): WorkcellInstrument => {
  const instrument = workcellInstruments.find((instr) => {
    const matchesType = instr.type === task.instrument.type;
    const matchesMapping =
      task.instrument.mappings?.length === 0 ||
      task.instrument.mappings?.some((mapping) =>
        instr.mappings.includes(mapping)
      );
    return matchesType && matchesMapping;
  });

  // SDK workcell doesn't have appropriate instrument
  if (!instrument) {
    throw new SdkMisconfigurationError(
      `No matching instrument/mappings found for task "${task.id}". Please verify appropriate instrument is included in workcell.`
    );
  }

  return instrument;
};

type InputsById = Record<string, InputType>;

export const getDataMode = (
  inputs: InputsById,
  targetInputName: keyof typeof DATA_MODE_BY_PARAM
): DataMode | undefined => {
  const targetInput = inputs?.[targetInputName];
  if (targetInput === undefined) return undefined;

  const isConnectorInput = 'task_id' in targetInput && targetInput.task_id;
  if (isConnectorInput) return DataMode.Connector;

  const isParameterRef =
    'parameter_id' in targetInput && targetInput.parameter_id;
  if (isParameterRef) return DataMode.Manual;

  return undefined;
};

export const getTaskAppliedParameters = (
  inputs: Record<string, InputType>
): Record<string, string> =>
  Object.entries(inputs).reduce<Record<string, string>>(
    (acc, [inputId, input]) => {
      if ('parameter_id' in input && input.parameter_id) {
        acc[inputId] = input.parameter_id;
      }
      return acc;
    },
    {}
  );

/** Converts primitive values to string, returns null for objects/arrays. */
export const safeToString = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value.toString();
  }
  return null;
};

export const getConditionalAppliedParameters = (
  task: Conditional
): Record<string, string> | null => {
  const { comparison } = task;
  if (comparison === 'Else') return null;

  const { rhs } = comparison;

  if ('parameter_id' in rhs) {
    rhs satisfies ParameterReference;

    // Canvas seems to only only allow param reference in lhs, not rhs. This is not consistent with the SDK
    // see https://automata-tech.atlassian.net/browse/G2-1929
    throw new UnsupportedFeatureError(
      'ParameterReference condition inputs are not currently supported in Canvas'
    );
  }

  if ('value' in rhs) {
    rhs satisfies StaticData;

    const parsedValue = safeToString(rhs.value);

    if (parsedValue === null) return null;

    return {
      source_value: parsedValue,
    };
  }

  if ('key' in rhs) {
    rhs satisfies ReferenceData;
    throw new UnsupportedFeatureError(
      'ReferenceData task inputs are not supported in Canvas'
    );
  }

  // TaskOutputReference should be ignored here, it's not relevant to applied params
  if ('task_id' in rhs) {
    rhs satisfies TaskOutputReference;
    return null;
  }

  // statically ensure all cases are handled
  rhs satisfies never;
  return null;
};

export const getScriptName = (
  task: CodeTask,
  functions: ScriptFunctionInfo[]
) => {
  const availableFunctionNames = functions.map((func) => func.function_name);
  const targetFunctionName = task.function.value;
  if (!availableFunctionNames.includes(targetFunctionName)) {
    throw new UnsupportedScriptError(
      `The script "${targetFunctionName}" is not supported in the current environment. \n Available functions: "${availableFunctionNames.join(
        ', '
      )}"`
    );
  }
  return targetFunctionName;
};

export const parseStringToBoolean = (
  value: string | number | true
): boolean | string | number => {
  if (typeof value === 'string') {
    const lowerValue = value.toLowerCase();
    if (lowerValue === 'true') return true;
    if (lowerValue === 'false') return false;
  }
  return value;
};

export const getDataExportParameters = (
  inputs: Record<string, InputType>
): Record<string, TaskParameterValue> =>
  Object.entries(inputs).reduce<Record<string, TaskParameterValue>>(
    (acc, [inputId, input]) => {
      if ('task_id' in input) {
        input satisfies TaskOutputReference;
        if (input.output_name) {
          acc[inputId] = input.output_name;
        }
        return acc;
      } else if ('value' in input) {
        input satisfies StaticData;
        // ignore undefined, arrays and objects - only include primitive values
        if (!input.value) return acc;
        if (Array.isArray(input.value)) return acc;
        if (typeof input.value === 'object' && input.value !== null) return acc;

        acc[inputId] = parseStringToBoolean(input.value);
        return acc;
      } else if ('parameter_id' in input) {
        input satisfies ParameterReference;
        throw new UnsupportedFeatureError(
          'ParameterReference data export inputs are not currently supported during SDK interoperability'
        );
      } else if ('key' in input) {
        input satisfies ReferenceData;
        throw new UnsupportedFeatureError(
          'ReferenceData task inputs are not supported in Canvas'
        );
      } else {
        input satisfies never;
        throw new SDKInteropError('This should be unreachable');
      }
    },
    {}
  );

/**
 * Resolves labware type from type string or definition URI.
 * @param type - Direct labware type or definition URI (namespace/loadName). Namespace is optional.
 * @param definitions - Labware definitions lookup
 * @returns Validated LabwareType or 'microplate' default
 */
export const getLabwareTypeFromLabwareItem = (
  type: string,
  definitions: LabwareDefByDefURI = getAllLabwareDefinitions()
): LabwareType => {
  // accepts direct labware type
  if (isLabwareType(type)) {
    return type;
  }

  // accepts labware definition URI, with and without namespace
  const [namespace, loadName] = type.includes('/')
    ? type.split('/', 2)
    : [null, type];

  const matchingDef = Object.values(definitions).find((def) => {
    if (namespace) {
      return def.namespace === namespace && def.loadName === loadName;
    } else {
      return def.loadName === loadName;
    }
  });

  if (!matchingDef) {
    const defaultLabwareType: LabwareType = 'microplate';
    logger.error(
      `No matching labware definition found for type "${type}". Using default type "${defaultLabwareType}".`
    );
    return defaultLabwareType;
  }

  return matchingDef.type;
};
